import React from 'react';
import { Tag, Tooltip } from 'antd';
import { TeamOutlined, FileTextOutlined, CheckCircleOutlined, BookOutlined } from '@ant-design/icons';

const TYPE_LABELS = { normal: 'Normal', trial: 'Deneme', makeup: 'Telafi' };

const SlotCard = ({ slot, onClick, onContextMenu, compact = false, showTime = false, isPast = true }) => {
  const { classroom_name, branch_name, capacity, total, attendance_count = 0, present_count = 0, project_count = 0, planned_absent_count = 0, type_counts, teachers, color, note, start_time, end_time } = slot;

  const isFull     = total >= capacity;
  const allAttended = attendance_count >= total && total > 0;
  const cardColor  = isFull ? '#ff4d4f' : (color || '#1677ff');

  const normalCount = type_counts?.normal  || 0;
  const trialCount  = type_counts?.trial   || 0;
  const makeupCount = type_counts?.makeup  || 0;
  const teacherNames = (teachers || []).map(t => t.name + ' ' + t.surname).join(', ');

  if (compact) {
    return (
      <div
        className="rounded border cursor-pointer select-none transition-shadow hover:shadow-sm h-full overflow-hidden"
        style={{ borderColor: cardColor, borderLeftWidth: 3, background: `${cardColor}14`, padding: '4px 6px' }}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        {/* Satır 1: Derslik + doluluk + ikonlar */}
        <div className="flex items-center justify-between gap-1">
          <span className="font-semibold truncate" style={{ fontSize: 11, color: '#1d3557' }}>
            {classroom_name}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {note && <FileTextOutlined style={{ fontSize: 9, color: '#6b7280' }} />}
            {isPast && (allAttended
              ? <CheckCircleOutlined style={{ fontSize: 10, color: '#52c41a' }} />
              : total > 0
                ? <span style={{ fontSize: 9, color: attendance_count > 0 ? '#fa8c16' : '#9ca3af', fontWeight: 700 }}>{attendance_count}/{total}</span>
                : null
            )}
            {isPast && present_count > 0 && (
              project_count >= present_count
                ? <BookOutlined style={{ fontSize: 10, color: '#52c41a' }} />
                : <span style={{ fontSize: 9, color: '#722ed1', fontWeight: 700 }}>{project_count}/{present_count}</span>
            )}
            <span style={{ fontSize: 10, fontWeight: 700, color: cardColor }}>{total}/{capacity}</span>
          </div>
        </div>

        {/* Satır 2: Tip sayıları */}
        {(normalCount > 0 || trialCount > 0 || makeupCount > 0) && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {normalCount > 0 && (
              <span style={{ fontSize: 9, background: '#e6f4ff', color: '#1677ff', borderRadius: 3, padding: '0 3px', fontWeight: 600 }}>
                {normalCount}N
              </span>
            )}
            {trialCount > 0 && (
              <span style={{ fontSize: 9, background: '#fffbe6', color: '#d48806', borderRadius: 3, padding: '0 3px', fontWeight: 600 }}>
                {trialCount}D
              </span>
            )}
            {makeupCount > 0 && (
              <span style={{ fontSize: 9, background: '#fff7e6', color: '#d46b08', borderRadius: 3, padding: '0 3px', fontWeight: 600 }}>
                {makeupCount}T
              </span>
            )}
            {planned_absent_count > 0 && (
              <span style={{ fontSize: 9, background: '#f9f0ff', color: '#722ed1', borderRadius: 3, padding: '0 3px', fontWeight: 600 }}>
                {planned_absent_count}P
              </span>
            )}
          </div>
        )}

        {/* Satır 3: Şube */}
        {branch_name && (
          <div className="truncate" style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
            {branch_name}
          </div>
        )}

        {/* Satır 4: Öğretmenler */}
        {teachers && teachers.length > 0 && (
          <div className="truncate" style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
            {teachers.map(t => t.name).join(', ')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border cursor-pointer select-none transition-shadow hover:shadow-md h-full overflow-hidden"
      style={{ borderColor: cardColor, borderLeftWidth: 4, background: `${cardColor}14`, padding: '6px 10px' }}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      {/* Satır 0: Saat (haftalık görünümde) */}
      {showTime && start_time && (
        <div className="text-xs font-semibold mb-1" style={{ color: '#111827' }}>
          {start_time.slice(0, 5)}{end_time ? ` – ${end_time.slice(0, 5)}` : ''}
        </div>
      )}

      {/* Satır 1: Derslik adı + doluluk + ikonlar */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-bold text-sm truncate" style={{ color: '#1d3557' }}>
          {classroom_name}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {note && (
            <Tooltip title="Sertifika Programı notu var">
              <FileTextOutlined style={{ fontSize: 12, color: '#6b7280' }} />
            </Tooltip>
          )}
          {isPast && (allAttended ? (
            <Tooltip title="Tüm yoklamalar alındı">
              <CheckCircleOutlined style={{ fontSize: 12, color: '#52c41a' }} />
            </Tooltip>
          ) : total > 0 ? (
            <Tooltip title={`${attendance_count}/${total} yoklama alındı`}>
              <span style={{ fontSize: 10, color: attendance_count > 0 ? '#fa8c16' : '#9ca3af', fontWeight: 600 }}>
                {attendance_count}/{total}
              </span>
            </Tooltip>
          ) : null)}
          {isPast && present_count > 0 && (
            project_count >= present_count ? (
              <Tooltip title="Tüm gelen öğrenciler için proje girildi">
                <BookOutlined style={{ fontSize: 12, color: '#52c41a' }} />
              </Tooltip>
            ) : (
              <Tooltip title={`${project_count}/${present_count} öğrenci için proje girildi`}>
                <span style={{ fontSize: 10, color: '#722ed1', fontWeight: 600 }}>
                  {project_count}/{present_count}
                </span>
              </Tooltip>
            )
          )}
          <span className="text-xs font-bold" style={{ color: cardColor }}>
            {total}/{capacity}
          </span>
        </div>
      </div>

      {/* Satır 2: Şube */}
      <div className="text-xs text-gray-500 mb-1 truncate">{branch_name}</div>

      {/* Satır 3: Ders tipleri */}
      <div className="flex flex-wrap gap-1 mb-1">
        {normalCount  > 0 && <Tag color="blue"   style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{normalCount} Normal</Tag>}
        {trialCount   > 0 && <Tag color="gold"   style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{trialCount} Deneme</Tag>}
        {makeupCount  > 0 && <Tag color="orange" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{makeupCount} Telafi</Tag>}
        {planned_absent_count > 0 && <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{planned_absent_count} Planlı</Tag>}
      </div>

      {/* Satır 4: Öğretmenler */}
      {teachers && teachers.length > 0 && (
        <Tooltip title={teacherNames}>
          <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
            <TeamOutlined />
            <span className="truncate">{teacherNames}</span>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default SlotCard;
